import {
  Erc4626PreviewRedeemAbi,
  Erc4626ReadAbi,
  TotalSupplyAbi,
} from '../abis/shared'
import type { LstRegistryEntry } from '../registry'
import { ONE_E18, toBigInt, type LstReader } from './shared'

/**
 * Generic ERC-4626 reader. Three calls:
 *   1. `totalAssets()`
 *   2. `totalSupply()`
 *   3. `convertToAssets(1e18)`
 *
 * The third gives a 1e18-scaled exchange rate without needing a
 * separate decimals branch. Used for real 4626 shares (pufETH, ynETH).
 */
export const readerErc4626 = (entry: LstRegistryEntry): LstReader => ({
  calls: [
    { address: entry.address, name: 'totalAssets', params: [] },
    { address: entry.address, name: 'totalSupply', params: [] },
    { address: entry.address, name: 'convertToAssets', params: [ONE_E18] },
  ],
  abis: [Erc4626ReadAbi, TotalSupplyAbi, Erc4626ReadAbi],
  parse: ([assets, supply, rate]) => {
    const totalAssets = toBigInt(assets)
    const totalSupply = toBigInt(supply)
    const exchangeRate = toBigInt(rate)
    if (
      totalAssets === undefined ||
      totalSupply === undefined ||
      exchangeRate === undefined
    ) {
      return undefined
    }
    return { totalAssets, totalSupply, exchangeRate }
  },
})

/**
 * ERC-4626 reader variant that derives the rate from `previewRedeem`
 * rather than `convertToAssets`. Identical call shape otherwise.
 *
 * Used for multi-asset vaults that revert on `convertToAssets` but
 * implement `previewRedeem` (YieldNest ynLSDe). The unit-of-account is
 * still 18-decimal (ETH-denominated), so the result is interchangeable
 * with the generic reader's `exchangeRate`.
 */
export const readerErc4626PreviewRedeem = (
  entry: LstRegistryEntry,
): LstReader => ({
  calls: [
    { address: entry.address, name: 'totalAssets', params: [] },
    { address: entry.address, name: 'totalSupply', params: [] },
    { address: entry.address, name: 'previewRedeem', params: [ONE_E18] },
  ],
  abis: [Erc4626PreviewRedeemAbi, TotalSupplyAbi, Erc4626PreviewRedeemAbi],
  parse: ([assets, supply, rate]) => {
    const totalAssets = toBigInt(assets)
    const totalSupply = toBigInt(supply)
    const exchangeRate = toBigInt(rate)
    if (
      totalAssets === undefined ||
      totalSupply === undefined ||
      exchangeRate === undefined
    ) {
      return undefined
    }
    return { totalAssets, totalSupply, exchangeRate }
  },
})
